import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Associations, ProfileItemResponse } from 'src/modules/invoice/models/invoiceTemplate';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { MemberType } from 'src/shared/models/invoiceMission/invoiceMission';
import { getAssociations } from 'src/shared/services/associationService';

// Urvalet i klartext, och om föreningsvalet överhuvudtaget ska visas. Ligger i en hook eftersom
// både urvalsfälten och sammanfattningen bredvid dem behöver samma svar, och de renderas på var
// sitt ställe i mallinformationen.
export const useInvoiceTemplateSelection = () => {
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const isMounted = useIsMounted();

  const associations: Associations[] = watch('associations') || [];
  const profilesIncludeItems: ProfileItemResponse[] = watch('profilesIncludeItems') || [];
  const profilesExcludeItems: ProfileItemResponse[] = watch('profilesExcludeItems') || [];

  // Har kunden bara en förening finns inget att välja mellan, då döljs föreningsvalet helt.
  // Antalet hämtas med en sida om en post, det är totalen vi är ute efter. Går det inte att
  // hämta visas valet ändå, hellre det än att dölja ett villkor kunden behöver.
  const [multipleAssociations, setMultipleAssociations] = useState(false);

  useEffect(() => {
    const fetchAssociationCount = async () => {
      try {
        const result = await getAssociations(undefined, { filter: '', pageIndex: 0, pageSize: 1, sortCol: '', sortDir: 'asc' });
        if (isMounted()) setMultipleAssociations((result.data.totalItems ?? 0) > 1);
      } catch (err) {
        ConsoleHelper.log(err);
        if (isMounted()) setMultipleAssociations(true);
      }
    };

    fetchAssociationCount();
  }, [isMounted]);

  // En mall som redan har föreningar valda visar dem alltid, annars skulle ett satt villkor bli osynligt.
  const showAssociations = multipleAssociations || associations.length > 0;

  // Datumen ligger antingen som Dayjs (nyss valt) eller som YYYYMMDD (hämtat från mallen).
  const toDate = (value: unknown) => {
    if (!value) return null;
    const parsed = dayjs.isDayjs(value) ? value : dayjs(String(value), 'YYYYMMDD');
    return parsed.isValid() ? parsed : null;
  };

  const toDisplayDate = (value: unknown) => {
    const parsed = toDate(value);
    return parsed ? parsed.format('YYYY-MM-DD') : '';
  };

  const memberTypeLabelKey = (value: number) => `invoice:adminInvoiceTemplatePage.memberType.${MemberType[value]?.toLowerCase() ?? 'unknown'}`;

  // Beskriver det som är valt just nu i formuläret, till skillnad från antalet som räknas av
  // API:et på den sparade mallen.
  const selectionClauses = (() => {
    const clauses: string[] = [];
    const tk = (key: string) => t(`invoice:adminInvoiceTemplatePage.templateInformation.${key}`);
    const tc = (key: string, values: Record<string, string>) => t(`invoice:adminInvoiceTemplatePage.templateInformation.${key}`, values) as string;

    if (watch('selection_id')) clauses.push(tk('clauseSavedSelection'));

    // Datumen beskriver medlemskapet, inte fakturan. Meningarna säger det rakt ut, eftersom ordet
    // förfallodatum annars läses som när fakturan ska betalas.
    const expireFrom = toDisplayDate(watch('expireDateFrom'));
    const expireTom = toDisplayDate(watch('expireDateTom'));
    if (expireFrom && expireTom && expireFrom !== expireTom) clauses.push(tc('clauseExpireRange', { from: expireFrom, to: expireTom }));
    else if (expireFrom || expireTom) clauses.push(tc('clauseExpireSingle', { date: expireFrom || expireTom }));

    const payedFrom = toDisplayDate(watch('payedDateFrom'));
    const payedTom = toDisplayDate(watch('payedDateTom'));
    if (payedFrom && payedTom && payedFrom !== payedTom) clauses.push(tc('clausePayedRange', { from: payedFrom, to: payedTom }));
    else if (payedFrom || payedTom) clauses.push(tc('clausePayedSingle', { date: payedFrom || payedTom }));

    const exitDate = toDisplayDate(watch('resdate'));
    if (exitDate) clauses.push(tc('clauseExitDate', { date: exitDate }));

    const memberType = watch('memberType');
    if (memberType !== undefined && memberType !== null) clauses.push(tc('clauseMemberType', { value: t(memberTypeLabelKey(memberType)) }));

    if (showAssociations)
      clauses.push(tc('clauseAssociations', { value: associations.length > 0 ? associations.map((a) => a.name).join(', ') : tk('allAssociations') }));

    if (profilesIncludeItems.length > 0)
      clauses.push(tc('clauseIncludedProfiles', { value: profilesIncludeItems.map((p) => `${p.groupName}: ${p.name}`).join(', ') }));

    if (profilesExcludeItems.length > 0)
      clauses.push(tc('clauseExcludedProfiles', { value: profilesExcludeItems.map((p) => `${p.groupName}: ${p.name}`).join(', ') }));

    return clauses;
  })();

  return { selectionClauses, showAssociations };
};
